import React, { createContext, useContext } from 'react'
import { useAppI18N, variantProvider } from '@/app'
import {
  View,
  Text,
  ActivityIndicator,
  ActionIcon,
  Image,
  DiscoverScoreModal,
  ProgressCircle,
} from '@/components'
import { useMemo } from '@codeleap/common'
import { ScoreTopic } from '@/types'
import { APIClient, analytics } from '@/services'
import {
  authWall,
  formatNumberToShort,
  getTopicBackground,
  useMediaQueryDown,
  useModal,
} from '@/utils'
import { Score } from '../atoms/Score'
import { parseScoreProgress } from '../../utils'
import { Organisation } from '@/features'

export type ClimateEngagementScoreProps = {
  variant?: 'modal' | 'withoutModal'
  organisation?: Organisation.T.Organisation
  score?: Organisation.T.Organisation['score']
  onExpand?: () => void
  showLastViewedText?: boolean
  showReviews?: boolean
  blurred?: boolean
  loading?: boolean
  topic?: ScoreTopic
}

const BREAKPOINT = 'small'

type TCESContext = ClimateEngagementScoreProps & {
  hasModal: boolean
}
const CESContext = createContext({} as TCESContext)

const useCESContext = () => {
  return useContext(CESContext)
}

const BlurredScores = () => {
  const { organisation, topic } = useCESContext()

  return (
    <View style={styles.blurWrapper}>
      <Image
        source={topic?.preview_web ?? topic?.preview_mobile}
        style={styles.blurImage}
      />
      <DiscoverScoreModal.Button style={styles.learnMore} />
      <DiscoverScoreModal organisation={organisation} />
    </View>
  )
}

type ScoresProps = {
  score?: Organisation.T.Organisation['score']
}

const formatOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}

const Scores = (props: ScoresProps) => {
  const { score } = props

  const { t } = useAppI18N()

  const { hasModal, topic } = useCESContext()

  const overallScore = score?.overall ?? 0

  const scoreRanks = APIClient.Answers.useScoreRanks()

  const categoryKeys = Object.keys(score?.categories ?? {})

  const { categories } = APIClient.Organisations.useCategories()

  const organisationRank = scoreRanks.data?.find(
    rank => overallScore >= rank.score
  )

  const isMid = useMediaQueryDown('mid')

  // const isReviewsEmpty = !score?.number_of_reviews

  // const wrapperStyles = hasModal ? styles.wrapper : styles.modalWrapper

  // if (isReviewsEmpty) {
  //   return <View variants={['alignCenter', 'justifyCenter']}>
  //     <View style={wrapperStyles}>
  //       <ScoreNotFound />
  //     </View>
  //   </View>
  // }

  return (
    <View variants={['column', 'gap:2', 'fullWidth', 'alignSelfCenter']}>
      <View
        variants={[
          'alignCenter',
          'justifyCenter',
          'gap:4',
          'fullWidth',
          'relative',
        ]}
      >
        <View
          variants={[
            'column',
            'alignCenter',
            'justifyCenter',
            (hasModal || isMid) && 'flex',
          ]}
        >
          <ProgressCircle
            progress={overallScore}
            image={
              organisationRank?.web_image || organisationRank?.mobile_image
            }
            variants={['noLabel', 'largeImage', 'large']}
            text={organisationRank?.label}
            color={topic?.color}
          />
          <View variants={['column', 'alignCenter', 'justifyCenter']}>
            <Text variants={['hx', 'color:neutral9']}>
              {parseScoreProgress(overallScore)}
            </Text>
            <Text
              variants={['p2', 'color:neutral5', 'marginTop:-1']}
              text={t('organisations.score.average')}
            />
          </View>
        </View>

        <View style={styles.verticalSeparator} />

        <View
          variants={[
            'alignCenter',
            'justifyCenter',
            (hasModal || isMid) && 'flex',
          ]}
        >
          <View variants={['column', 'gap:2']}>
            {categoryKeys?.map(categoryId => {
              const category = categories[categoryId]

              const categoryScore = score?.categories?.[categoryId]?.total ?? 0

              return (
                <Score
                  key={category?.name}
                  name={category?.name}
                  score={categoryScore}
                  color={topic?.color}
                  img={category?.web_icon || category?.mobile_icon}
                />
              )
            })}
          </View>
        </View>
      </View>
    </View>
  )
}

const Reviews = (props: ScoresProps & { showLastViewedText?: boolean }) => {
  const { score, showLastViewedText = false } = props

  const { t } = useAppI18N()

  if (!score) return null

  const lastViewed = score?.last_review_date
    ? new Date(score?.last_review_date)?.toLocaleDateString(
        undefined,
        formatOptions
      )
    : null
  const showOverviewText =
    !!score.last_review_date &&
    !!score.number_of_reviews &&
    !!score.number_of_reviewers
  return (
    <>
      {showOverviewText && !showLastViewedText && (
        <Text variants={['p4', 'color:neutral5']}>
          {t(
            `organisations.score.${
              score.number_of_reviews !== 1 ? 'answers' : 'answer'
            }`,
            {
              nReviews: formatNumberToShort(score.number_of_reviews),
            }
          )}
          {'\u00A0'}
          {t('from')}
          {'\u00A0'}
          {t(
            `organisations.score.${
              score.number_of_reviewers !== 1 ? 'reviewers' : 'reviewer'
            }`,
            {
              nReviewers: formatNumberToShort(score.number_of_reviewers),
            }
          )}
        </Text>
      )}
      {showLastViewedText && (
        <Text variants={['p4', 'color:neutral5']}>
          {score?.number_of_reviews}{' '}
          {t(
            `organisations.score.${
              score?.number_of_reviews !== 1 ? 'lastReviews' : 'lastReview'
            }`,
            {
              nLastReviewed: lastViewed,
            }
          )}
        </Text>
      )}
    </>
  )
}

export const ClimateEngagementScore = (props: ClimateEngagementScoreProps) => {
  const {
    variant = 'modal',
    organisation,
    showLastViewedText = false,
    score = organisation?.score,
    showReviews = true,
    onExpand,
    blurred,
    loading = false,
    topic,
  } = props

  const _modal = useModal()

  const { categories, subcategories } = APIClient.Organisations.useCategories()

  const overviewData = useMemo(() => {
    if (score && categories) {
      const formattedCategories = Object.keys(categories).reduce(
        (acc, category) => {
          return {
            ...acc,
            [categories[category].name]:
              score.categories[category].total?.toFixed() || 0,
          }
        },
        {}
      )

      return { average_score: score.overall.toFixed(), ...formattedCategories }
    }
  }, [score, categories])

  const hasModal = variant === 'modal'

  const wrapperStyles = hasModal ? styles.wrapper : styles.modalWrapper

  if (!categories || !subcategories || loading) {
    return (
      <View variants={['center', 'flex']}>
        <ActivityIndicator debugName={'ClimateEngagementScore Indicator'} />
      </View>
    )
  }

  const onToggle = authWall(() => {
    if (!_modal.visible) {
      analytics.track('climate_empowerment_score', overviewData)
    }

    onExpand?.()
  })

  const paddings = blurred ? [] : ['paddingHorizontal:1.5', 'paddingVertical:2']

  return (
    <CESContext.Provider value={props}>
      <View
        variants={['center', 'fullWidth', 'border-radius:medium', ...paddings]}
        style={{ backgroundColor: getTopicBackground(topic?.color) }}
      >
        <View css={wrapperStyles} variants={['column', 'gap:2']}>
          {blurred ? (
            <BlurredScores />
          ) : (
            <>
              <ScoreHeader
                title={topic?.label}
                onExpand={onExpand ? onToggle : null}
              />

              <Scores score={score} />
              <View
                variants={['column', 'justifyCenter', 'alignCenter', 'gap:2']}
                responsiveVariants={{
                  [BREAKPOINT]: [
                    'column',
                    'justifyCenter',
                    'alignCenter',
                    'gap:2',
                  ],
                }}
              >
                <Reviews
                  score={score}
                  showLastViewedText={showLastViewedText}
                />
              </View>
            </>
          )}
        </View>
      </View>
    </CESContext.Provider>
  )
}

type ScoreHeaderProps = {
  onExpand?: () => void
  title?: string
}
export function ScoreHeader(props: ScoreHeaderProps) {
  const { onExpand, title } = props

  const { t } = useAppI18N()

  return (
    <View
      variants={[
        'alignCenter',
        'justifySpaceBetween',
        'alignSelfStart',
        'fullWidth',
      ]}
    >
      <Text
        variants={['h3', 'color:neutral9']}
        responsiveVariants={{ [BREAKPOINT]: ['h2', 'color:neutral9'] }}
      >
        {title ?? t('organisations.scoreTitle')}
      </Text>
      {onExpand && (
        <ActionIcon
          name="arrow-right"
          variants={['minimal']}
          onPress={onExpand}
          debugName={'myOrganisation arrow-right icon'}
        />
      )}
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      maxWidth: '460px',
      width: '100%',
    },
    modalWrapper: {
      maxWidth: '460px',

      width: '100%',
    },
    verticalSeparator: {
      width: '1px',
      // position: 'absolute',
      // height: '90%',
      // top: '10%',
      // bottom: '10%',
      ...theme.spacing.marginVertical(2),
      alignSelf: 'stretch',
      justifySelf: 'center',
      backgroundColor: theme.colors.neutral3,
    },
    placeholderCircle: {
      width: '130px',
      height: '130px',
      backgroundColor: theme.colors.primary3,
      borderRadius: theme.borderRadius.rounded,
    },
    tinyPlaceholderCircle: {
      width: '64px',
      height: '64px',
      backgroundColor: theme.colors.primary3,
      borderRadius: theme.borderRadius.rounded,
    },
    learnMore: {
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%,-50%)',
      zIndex: 1,
    },
    blurWrapper: {
      maxWidth: '460px',
      position: 'relative',
    },
    blurImage: {
      width: '100%',
      aspectRatio: 776 / 620,
      zIndex: 0,
    },
  }),
  true
)
